"use client";

import { useTranslations } from "next-intl";
import { type ReactNode, useState } from "react";
import { useResponsiveCount } from "@/lib/hooks/use-responsive-count";
import {
  getEnabledPaymentMethods,
  type PaymentMethodKey,
} from "@/lib/schemas/paycode/payment-methods";
import {
  usePaymentMethods,
  useSelectedPaymentMethod,
  useSetSelectedPaymentMethod,
} from "@/lib/store/checkout";
import { cn } from "@/lib/utils";
import { HeroIcon } from "../hero-icon";
import { LucideIcon } from "../lucide-icon";
import { SpeiIcon } from "../spei-icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Skeleton } from "../ui/skeleton";

interface PaymentMethodMeta {
  icon?: ReactNode;
}

const PAYMENT_METHODS_META: Record<PaymentMethodKey, PaymentMethodMeta> = {
  card: {
    icon: <HeroIcon name="credit-card" className="h-5 w-5" />,
  },
  cash: {
    icon: <HeroIcon name="banknotes" className="h-5 w-5" />,
  },
  spei: {
    icon: <SpeiIcon className="h-6 w-6" />,
  },
  tpvBarcode: {
    icon: <LucideIcon name="scan-barcode" className="h-5 w-5" />,
  },
  codi: {
    icon: <HeroIcon name="qr-code" className="h-5 w-5" />,
  },
  cie: {
    icon: <LucideIcon name="banknote-arrow-up" className="h-5 w-5" />,
  },
};

export const PaymentMethodPicker = () => {
  const t = useTranslations("methods");
  const methods = usePaymentMethods();
  const select = useSetSelectedPaymentMethod();
  const selected = useSelectedPaymentMethod();

  const [order, setOrder] = useState<PaymentMethodKey[]>([]);

  const baseEnabled = methods ? getEnabledPaymentMethods(methods) : [];
  const enabled = [
    ...order.filter((method) => baseEnabled.includes(method)),
    ...baseEnabled.filter((method) => !order.includes(method)),
  ];

  const { containerRef, visibleCount, columns, hasOverflow } =
    useResponsiveCount({
      count: enabled.length,
      minItemWidth: 112,
      gap: 1,
    });

  if (enabled.length < 2) return null;

  const visible = enabled.slice(0, visibleCount);
  const overflow = enabled.slice(visibleCount);

  const selectFromOverflow = (method: PaymentMethodKey) => {
    const next = [...enabled];
    const overflowIndex = next.indexOf(method);
    const lastVisibleIndex = visibleCount - 1;
    next[overflowIndex] = next[lastVisibleIndex];
    next[lastVisibleIndex] = method;
    setOrder(next);
    select(method);
  };

  return (
    <div
      ref={containerRef}
      className="grid h-12 w-full gap-px overflow-hidden rounded-lg border border-border bg-border"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {visible.map((method, index) => {
        const meta = PAYMENT_METHODS_META[method];
        const active = method === selected;
        const isLast = visible.length - 1 === index;
        return (
          <button
            type="button"
            key={`visible-method-${method}`}
            className={cn(
              "flex min-w-0 cursor-pointer items-center justify-center gap-x-1 truncate bg-background px-3 text-sm",
              active && "relative z-10 bg-top ring-1 ring-accent ring-inset",
              index === 0 && "rounded-l-lg",
              isLast && !hasOverflow && "rounded-r-lg",
            )}
            onClick={() => select(method)}
          >
            <span className={cn("text-muted", active && "text-accent")}>
              {meta.icon}
            </span>
            <span className={cn("text-muted", active && "text-foreground")}>
              {t(method)}
            </span>
          </button>
        );
      })}

      {hasOverflow && (
        <DropdownMenu>
          <DropdownMenuTrigger className="group">
            <div className="flex h-full min-w-0 cursor-pointer items-center justify-center gap-x-1 truncate rounded-r-lg bg-background px-3 text-muted text-sm group-data-[state=open]:text-foreground">
              {t("more")}
              <span className="group-data-[state=open]:-rotate-180 transition-transform duration-200">
                <HeroIcon
                  name="chevron-down"
                  iconStyle="micro"
                  className="h-4 w-4"
                />
              </span>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="border-border bg-top">
            {overflow.map((method) => {
              const meta = PAYMENT_METHODS_META[method];
              return (
                <DropdownMenuItem
                  key={`dm-item-${method}`}
                  onClick={() => selectFromOverflow(method)}
                >
                  {meta.icon}
                  {t(method)}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

// ponytail: single bar; column count depends on runtime measurement, height is what matters
export const PaymentMethodPickerSkeleton = ({
  count = 2,
}: {
  count?: number;
}) => {
  if (count < 2) return null;
  return <Skeleton className="h-12 w-full rounded-lg" />;
};
